"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.YourInfoStep = void 0;
const Step_1 = require("../types/Step");
const DetailsStep_1 = require("./DetailsStep");
const CaptchaHandler_1 = require("../CaptchaHandler");
const Utils_1 = require("../Utils");
const cleave_js_1 = __importDefault(require("cleave.js"));
const autocompleter_1 = __importDefault(require("autocompleter"));
/**
 * /order2 gate: collects address + first/last name + phone + email, an optional
 * promo code and the optional marketing consent. Every field prefills from its
 * URL param through PASS_THRU (?address=&firstName=&lastName=&phone=&email=&promo=);
 * the promo box additionally falls back to the code the visitor carries in the
 * shared .wire3.com store — see promoCode(). The Details step re-shows
 * name/email/phone, prefilled from what is typed here. The abMode
 * 'address-only'/'contact-only' modes are retired on /order2 — those branches
 * were stripped from this step (the partial always renders data-mode="default").
 */
class YourInfoStep extends Step_1.Step {
    constructor(state, idSuffix = '') {
        super();
        this.FORM_STATE = state;
        this.form = document.getElementById(`your-info${idSuffix}`);
        this.promoInput = this.form.elements['promo'];
        this.autosavePrompt = document.getElementById(`autosave${idSuffix}`);
        this.submit = document.getElementById(`your-info-submit${idSuffix}`);
        this.init();
    }
    init() {
        this.unlock();
        this.submit.addEventListener('click', async (e) => {
            // validate form first
            e.preventDefault();
            let isValid = this.submissionHandler(e);
            if (!isValid)
                return;
            /* okay, the form is valid, the next step is unlocked, lets try and submit */
            // save submissions
            this.storeState();
            // execute reCAPTCHA - can take a while, so show some loading state
            window['grecaptcha']?.execute();
            (0, Utils_1.setLoadingButton)(this.submit, true, 'Submitting...');
            let tryCount = 0;
            while (window['grecaptcha']?.getResponse() === '') {
                if (tryCount > 20) {
                    // took too long, something is wrong
                    (0, Utils_1.setLoadingButton)(this.submit, false);
                    (0, CaptchaHandler_1.captchaOnError)();
                    tryCount = 0;
                    return;
                }
                // wait for reCAPTCHA to be resolved
                await new Promise(res => setTimeout(res, 500));
                tryCount++;
            }
            // reCAPTCHA resolved, remove loading state, submit the profile, and move to the next step
            (0, Utils_1.setLoadingButton)(this.submit, false);
            this.submitProfile();
            this.FORM_STATE.frame.stepper.next();
            (0, Utils_1.sendScrollAdjustment)();
        });
        // check for autosave cookie
        let autosave = (0, Utils_1.getCookie)('autosave');
        if (autosave) {
            // show the autosave prompt
            this.autosavePrompt.classList.remove('d-none');
            // add event listener to the buttons
            this.autosavePrompt.addEventListener('click', (e) => {
                const target = e.target;
                // ignore clicks on the dismiss
                if (!target || target.matches('.dismiss, .dismiss .fa, .dismiss .fa use')) {
                    return;
                }
                // decode the cookie, and populate the form
                try {
                    autosave = JSON.parse(atob(autosave));
                    // there's probably a better way to do this but whatevs
                    if (this.form.elements['address']) {
                        this.form.elements['address'].value = autosave?.address || "";
                    }
                    this.form.elements['first-name'].value = autosave?.firstName || "";
                    this.form.elements['last-name'].value = autosave?.lastName || "";
                    this.form.elements['phone'].value = autosave?.phoneNumber || "";
                    this.form.elements['email'].value = autosave?.email || "";
                    // promo only when this visit brought none of its own — a ?promo= on the URL
                    // the customer just clicked outranks a code saved a week ago
                    const savedPromo = autosave?.promo || "";
                    if (savedPromo && !this.promoInput?.value) {
                        this.promoInput.value = savedPromo;
                    }
                    if (this.form.elements['marketing-opt-in']) {
                        this.form.elements['marketing-opt-in'].checked = autosave?.marketingOptIn || false;
                    }
                    // dismiss the prompt
                    // hide the prompt ITSELF — on the gate layout its parent is the <form>,
                    // so the old parentElement hide would blank the whole form
                    this.autosavePrompt.classList.add('d-none');
                }
                catch (ignored) { }
            });
        }
        // promo prefill: the input already renders ?promo= server-side (PASS_THRU). When this
        // landing carried none, fall back to the code the visitor picked up on an earlier
        // wire3.com page and is carrying in the shared store — see promoCode().
        if (this.promoInput && !this.promoInput.value) {
            this.promoInput.value = (0, Utils_1.promoCode)(this.FORM_STATE);
        }
        // mask phone input
        const cleave = new cleave_js_1.default('#phone', {
            numericOnly: true,
            blocks: [0, 3, 0, 3, 4],
            delimiters: ["(", ")", " ", "-"]
        });
        // setup address autocomplete
        const addressInput = this.form.elements['address'];
        const smartyOptions = new URLSearchParams({
            key: '109492307109888282',
            source: 'all',
            include_only_states: 'FL;GA;SC',
            prefer_states: 'FL',
            max_results: '6'
        });
        // When a suggestion bundles multiple secondary units (e.g. "... Unit (21 entries)"),
        // selecting it drills into Smarty's secondary expansion rather than accepting the
        // address. `unitSelected` is the `selected` lookup string and `unitPrefix` is the
        // address text the query must still contain for the drill-down to stay active.
        let unitSelected = null;
        let unitPrefix = null;
        const ac = (0, autocompleter_1.default)({
            input: addressInput,
            minLength: 5,
            emptyMsg: 'No addresses found',
            fetch: async (text, update) => {
                let suffix = "";
                if (unitSelected && unitPrefix && text.includes(unitPrefix)) {
                    suffix = `&selected=${encodeURIComponent(unitSelected)}`;
                }
                try {
                    let res = await fetch(`https://us-autocomplete-pro.api.smartystreets.com/lookup?${smartyOptions.toString()}&search=${encodeURIComponent(text)}${suffix}`);
                    let data = await res.json();
                    let suggestions = data.suggestions?.map(s => { return { label: this.buildAddress(s), value: s }; });
                    // cap the dropdown at 8 entries (no scrollbar); typing narrows to find any others
                    update((suggestions || []).slice(0, 8));
                }
                catch (ignored) { }
            },
            onSelect: (item) => {
                const v = item.value;
                // multiple secondary entries → drill into the unit list instead of accepting
                if (v?.entries > 1) {
                    // designator without the " (# entries)" suffix buildAddress appended
                    const designator = (v.secondary ?? '').replace(/\s*\(\d+\s*entries\)/i, '').trim();
                    // Smarty `selected` expansion string: "<street> <designator> (<entries>) <city> <state> <zip>"
                    unitSelected = `${v.street_line} ${designator} (${v.entries}) ${v.city} ${v.state} ${v.zipcode}`;
                    unitPrefix = `${v.street_line} ${designator}`.trim();
                    // prefill "<street> <designator> " and drop the caret at the unit spot
                    addressInput.value = `${unitPrefix} `;
                    addressInput.focus();
                    const caret = addressInput.value.length;
                    addressInput.setSelectionRange(caret, caret);
                    // re-run the lookup so the individual units populate the dropdown.
                    // Deferred to the next tick: the library calls clear() right after
                    // onSelect (which bumps its internal fetch counter) and would otherwise
                    // discard a synchronous fetch's async results before they can render.
                    setTimeout(() => ac.fetch(), 0);
                    return;
                }
                // normal selection → clear any drill-down state and accept the address
                unitSelected = null;
                unitPrefix = null;
                addressInput.value = item.label ?? '';
            },
            render: (item, currentValue) => {
                if (!item.label)
                    return;
                const itemEl = document.createElement('div');
                // bold the first matching run (escape regex metacharacters from the current value)
                const escaped = currentValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const match = escaped ? item.label.match(new RegExp(escaped, 'i')) : null;
                if (match && match.index !== undefined && match[0].length) {
                    const strong = document.createElement('strong');
                    strong.textContent = match[0];
                    itemEl.append(document.createTextNode(item.label.slice(0, match.index)), strong, document.createTextNode(item.label.slice(match.index + match[0].length)));
                }
                else {
                    itemEl.textContent = item.label;
                }
                return itemEl;
            },
            debounceWaitMs: 200,
            className: 'autocomplete-suggestions'
        });
        // autofill trigger
        if (this.FORM_STATE.config.autofill) {
            setTimeout(() => {
                this.submit.click();
            }, 200);
            const optIn = Array.from(this.form.elements).find(el => el.type === 'checkbox');
            if (optIn) {
                optIn.addEventListener('change', () => {
                    if (optIn.checked) {
                        this.submit.click();
                    }
                });
            }
        }
        if (this.FORM_STATE.config.devMode) {
            console.info(`[OF] YourInfoStep loaded.`);
        }
    }
    onShow() {
        if (this.isFirstShow()) {
            // first show event
            (0, Utils_1.sendEvent)('firstShowYourInfo', this.FORM_STATE);
            // trigger autofill
            if (this.FORM_STATE.config.autofill) {
            }
        }
        else {
            (0, Utils_1.sendScrollAdjustment)();
        }
    }
    // https://www.smarty.com/docs/cloud/us-autocomplete-pro-api
    buildAddress(suggestion) {
        let whiteSpace = "";
        if (suggestion.secondary) {
            if (suggestion.entries > 1) {
                suggestion.secondary += " (" + suggestion.entries + " entries)";
            }
            whiteSpace = " ";
        }
        return suggestion.street_line + whiteSpace + suggestion.secondary + " " + suggestion.city + ", " + suggestion.state + " " + suggestion.zipcode;
    }
    /**
     * Whether the form is valid.
     */
    validate() {
        // store validity + used to focus on the first invalid field
        let hasError = false;
        // validate every form control
        for (let field of Array.from(this.form.elements)) {
            // ignore submit buttons
            if (field instanceof HTMLInputElement) {
                // reset validation state
                field.classList.remove('is-invalid', 'is-valid', 'untouched');
                // an optional field left blank stays neutral — no green tick on an empty promo box
                if (!field.required && field.value === '') {
                    continue;
                }
                // mark field as valid/invalid
                if (!field.validity.valid) {
                    field.classList.add('is-invalid');
                    // focus on the first invalid field
                    if (!hasError) {
                        hasError = true;
                        field.focus();
                    }
                }
                else {
                    field.classList.add('is-valid');
                }
            }
        }
        return !hasError;
    }
    /**
     * Handle the form submission event. Validates the form and shows errors if any.
     * @param {Event} e - The form submission event.
     * @return {boolean} - Whether the form is valid.
     */
    submissionHandler(e) {
        const isValid = this.validate();
        if (isValid) {
            // hide errors + unlock the next step
            this.FORM_STATE.steps[1].unlock();
        }
        else {
            // nope, show error + lock the next step
            // TODO: show some error message to the user
            this.FORM_STATE.steps[1].lock();
        }
        return isValid;
    }
    /* Make the UGPS API call */
    async submitProfile() {
        // lock address field to prevent changes, and update <small> to indicate it's locked
        this.form.elements['address'].readOnly = true;
        this.form.elements['address'].nextElementSibling.innerText = `You cannot change the address field once locked in – please reload to change.`;
        // reset service options state (steps[1] is always PlanStep on /order2, gate flow)
        this.FORM_STATE.steps[1].resetOffers();
        // event
        (0, Utils_1.sendGEvent)('generate_lead', this.FORM_STATE);
        // UGPS call
        try {
            let res = await fetch((0, Utils_1.apiUrl)(`/api/ugps`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    address: this.form.elements['address'].value,
                    firstName: this.form.elements['first-name'].value.trim(),
                    lastName: this.form.elements['last-name'].value.trim(),
                    phoneNumber: this.form.elements['phone'].value,
                    email: this.form.elements['email'].value,
                    marketingOptIn: this.form.elements['marketing-opt-in']?.checked ?? false,
                    marketingOptInVersion: this.form.elements['marketing-opt-in']?.dataset.consentVersion ?? null,
                    // promo/coupon: whatever the gate's promo box holds — it was prefilled from
                    // ?promo= / the carried store and the customer may have typed over it. It has
                    // to ride WITH the address: the portal prices the offers in this response
                    // against it.
                    couponCode: this.promoValue(),
                    // cookies — profileUuid is the v4 spine (the hub forwards it WITHOUT flipping
                    // the portal A/B switch, which keys on the `uuid` field)
                    profileUuid: this.FORM_STATE.cookies.uuid,
                    // this landing's ?agent= wins over the cookie — see agentCredit()
                    agent: (0, Utils_1.agentCredit)(this.FORM_STATE),
                    // v4: marketing attribution envelope
                    attribution: this.FORM_STATE.attribution,
                    // reCAPTCHA
                    "g-recaptcha-response": window['grecaptcha']?.getResponse(),
                    v2: true
                })
            });
            let data = await res.json();
            if (res.ok && (data?.success != false)) {
                this.FORM_STATE.ugpsData = data;
                // event
                (0, Utils_1.sendGEvent)((this.FORM_STATE.ugpsData?.code === 'OK' || this.FORM_STATE.ugpsData?.code === 'N/A' ?
                    `qualify_lead` : `disqualify_lead`), this.FORM_STATE);
                (0, Utils_1.sendEvent)('checkedAddress', this.FORM_STATE);
            }
            else {
                console.warn(`[OF] UGPS ingest error:`, data, res);
                throw new Error('Unsuccessful response from UGPS');
            }
        }
        catch (err) {
            console.warn(`[OF] UGPS submission error:`, err);
        }
        // COMMERCIAL hand-off. /order2 sells residential only, so a business address leaves for the
        // regular form with everything typed so far prefilled — the customer never re-enters the
        // address. The lead is already recorded: this runs after the UGPS ingest above.
        //
        // Two exemptions. customerHub is an existing customer changing their plan, not a new order
        // (that path keeps the business-name row on Plan/Details). interestOnly is a marketing
        // campaign that deliberately collects interest without showing offers — bouncing it to a
        // full order form would defeat the campaign.
        if (this.FORM_STATE.ugpsData?.rdi === 'COMMERCIAL'
            && !this.FORM_STATE.config.customerHub
            && !this.FORM_STATE.config.interestOnly) {
            const url = (0, Utils_1.regularOrderFormUrl)(this.FORM_STATE);
            // best effort — the navigation below may cut the beacon short, but `checkedAddress`
            // above already carries the COMMERCIAL rdi if this one never lands
            (0, Utils_1.sendEvent)('commercialRedirect', this.FORM_STATE, { to: url });
            (0, Utils_1.navigateAway)(url);
            return;
        }
        if (this.FORM_STATE.config.interestOnly) {
            this.FORM_STATE.steps[1].buildInterestOnly();
            return;
        }
        else {
            this.FORM_STATE.steps[1].buildOffers();
        }
    }
    /**
     * The promo/coupon code this gate is submitting: the promo box, else the code the
     * visitor carries in the shared .wire3.com store (promoCode()) — the fallback covers
     * the `?hide_promo_code=1` landings, where the box exists but is never shown.
     */
    promoValue() {
        return (this.promoInput?.value || '').trim() || (0, Utils_1.promoCode)(this.FORM_STATE);
    }
    /**
     * Store the state of this step in FORM_STATE.
     */
    storeState() {
        // optional marketing opt-in. the consent version is server-injected onto the
        // checkbox and echoed back for tracking.
        const marketingOptInEl = this.form.elements['marketing-opt-in'];
        const marketingOptIn = marketingOptInEl?.checked ?? false;
        const marketingOptInVersion = marketingOptInEl?.dataset.consentVersion ?? null;
        // gather profile info, and store it in FORM_STATE
        const profileSummary = {
            address: this.form.elements['address'].value,
            firstName: this.form.elements['first-name'].value.trim(),
            lastName: this.form.elements['last-name'].value.trim(),
            phoneNumber: this.form.elements['phone'].value,
            email: this.form.elements['email'].value,
            promo: this.promoValue(),
            marketingOptIn,
            marketingOptInVersion
        };
        this.FORM_STATE.userInput.profile = profileSummary;
        // set `autosave` cookie (b64 string), expires in 7 days
        (0, Utils_1.setCookie)('autosave', btoa(JSON.stringify(profileSummary)), 7);
        // also update the summary in the DetailsStep (resolved by instance — never
        // by hardcoded index — per the /order2 step-roster contract)
        const detailsStep = this.FORM_STATE.steps.find(s => s instanceof DetailsStep_1.DetailsStep);
        detailsStep?.setSummaryProfile(profileSummary);
    }
}
exports.YourInfoStep = YourInfoStep;
